import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { Breakpoint, WindowEventService } from '@shared/services/window.event.service';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { CompanyBanner } from '@shared/models/company.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorInputs, ErrorService } from '@shared/services/error.service';
import { mergeAllChildRouteData, RouteData } from '@app/app-routing.module';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CourseContentService, Customisations } from '@app/courseContent/courseContent.service';

@Component({
  selector: 'pl-shell',
  templateUrl: './shell.component.html',
})
export class ShellComponent implements OnInit {
  @ViewChild('sidenav') public sidenav!: MatSidenav;
  public companyBannerOptions: Signal<CompanyBanner | null> = computed(
    () => this.participantService.participant()?.companyBanner || null,
  );

  public hideMenuFromRouteData: WritableSignal<boolean> = signal(false);
  public hideProfileSectionFromRouteData: WritableSignal<boolean> = signal(false);
  public disableLinkOnPlLogoFromRouteData: WritableSignal<boolean> = signal(false);
  public handleScrollWithinComponent: WritableSignal<boolean> = signal(false);

  public customisations: Signal<Customisations> = computed(() => this.courseContentService.customisations());
  public error: Signal<ErrorInputs | null> = computed(() => this.errorService.getError());

  public mode: WritableSignal<MatDrawerMode> = signal<MatDrawerMode>('side');

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private activatedRoute: ActivatedRoute,
    private courseContentService: CourseContentService,
    private errorService: ErrorService,
    private participantService: ParticipantService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private windowEventService: WindowEventService,
  ) {
    this.errorService.setShellChangeDetectorRef(this.ref);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => {
          return event instanceof NavigationEnd;
        }),
        switchMap((): Observable<RouteData> => {
          const routeData: RouteData = mergeAllChildRouteData(this.activatedRoute);
          return of(routeData);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: RouteData): void => {
        this.hideMenuFromRouteData.set(!!event.hideMenu);
        this.hideProfileSectionFromRouteData.set(!!event.hideProfileSection);
        this.disableLinkOnPlLogoFromRouteData.set(!!event.disableLinkOnPlLogo);
        this.handleScrollWithinComponent.set(!!event.handleScrollWithinComponent);
      });
  }

  public ngOnInit(): void {
    this.windowEventService
      .getWindowResizeEvent()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size: number): void => {
        this.mode.set(size < Breakpoint.lg ? 'over' : 'side');
      });

    this.participantService
      .getToggleLeftMenu()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.sidenav.toggle();
      });
  }

  public onStepMenuItemClicked(): void {
    if (this.mode() === 'over') {
      this.sidenav.close();
    }
  }

  public onScrollEnd(): void {
    this.windowEventService.addEventShellComponentHasFinishedScrolling();
  }
}
